import { appSettings } from "src/provider/config/constant";
import { ReactElement } from "react";
import { RouteData } from "src/model";
import access from "./localAccess";

const routeData = (
  path: string,
  name: string,
  component: ReactElement<any, any>
): RouteData => {
  return {
    routeProps: {
      path,
      element: component,
    },
    name,
  };
};

const toTitleCase = (text: string) => {
  const splittedText = text.split(" ");
  const formattedText = [];

  for (let item of splittedText) {
    item = item.toLowerCase();
    formattedText.push(item[0].toUpperCase() + item.substring(1, item.length));
  }

  return formattedText.join(" ");
};

const splitCamelCase = (text: string, isTitleCase?: boolean) => {
  let result = text.replace(/([a-z])([A-Z])/g, "$1 $2");
  result = isTitleCase ? result[0].toUpperCase() + result.substring(1) : result;
  return result;
};

const getPageTitle = () => {
  let route = window.location.href.split("/").pop()!;
  let pageTitle = route
    ? route.split("-").join(" ").split("_").join(" ")
    : "Home";
  return toTitleCase(pageTitle);
};

export const erroMessage = (text: any) => {
  const isOnline = access.getInternetStatus();
  return !isOnline
    ? "No Internet Connection"
    : `Message: ${text || "An error occured"}`;
};

export const requestMessage = (resp: any, text = "") => {
  return (
    resp?.responseDescription || resp?.message || text || "Something went wrong"
  );
};

export const catchErrMsg = (err: any) => {
  let message =
    err?.response?.data?.responseDescription ||
    err?.response?.data?.message ||
    `Something went wrong, Please try again later. If problem persist kindly contact our support.`;

  if (err.response?.data?.errors)
    message = retrieveAPIValidationMsg(err.response.data.errors);

  if (
    err.response?.status === 401 ||
    message === appSettings.SESSION_EXPIRATION_MSG ||
    ["Invalid session token.", "session has expired."].includes(message)
  ) {
    access.getLogoutHandler()();
  }
  return message;
};

export const onboardingProgressBarLength = (path: string) => {
  const credentialsCreation = [
    "/onboarding/credentials-creation",
    "/onboarding/security-questions",
    "/onboarding/referral",
  ];
  if (path === "/onboarding/bvn-verification") return "0";
  else if (path === "/onboarding/user-verification") return "26";
  else if (path === "/onboarding/info-confirmation") return "50";
  else if (path === "/onboarding/identity-verification") return "73";
  else if (credentialsCreation.includes(path)) return "100";
  else return "0";
};

export const moneyFormat = (
  val: number | string,
  twoDecimalPlace?: boolean,
  currency: string = "NGN"
) => {
  const currencyMap: Record<string, string> = {
    NGN: "₦",
    GMD: "GMD",
  };
  const symbol = currencyMap[currency] || currency;

  var formatter = new Intl.NumberFormat("en-NG", {
    currency: currency,
    minimumFractionDigits: twoDecimalPlace ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return `${symbol} ${formatter.format(Number(val))}`;
};

export const handleAmountChange = (
  value: string,
  source: "input" | "selection",
  setAmount: (value: string) => void,
  setInput: (input: any) => void,
  input: any,
  target = "amount"
) => {
  let val = value.replace(/,/g, "");
  if (
    !isNaN(Number(val)) ||
    (val.includes(".") && !isNaN(Number(val.replace(/\./g, ""))))
  ) {
    const parts = val.split(".");
    let formattedValue = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    if (parts[1] !== undefined) {
      const decimalPart = parts[1].slice(0, 2);
      formattedValue += `.${decimalPart}`;
    }

    setAmount(formattedValue);
    setInput({ ...input, [target]: source === "input" ? val : value });
  }
};

export const loglite = (data: any, description?: string) => {
  if (description) console.log(`====== ${description} =======`);
  console.log(data);
};

export const getCharacterToDispay = (
  character = "",
  acceptedCharacterLength = 20
) => {
  let result = character;

  if (character.length > acceptedCharacterLength) {
    result = `${result.substring(0, acceptedCharacterLength)}...`;
  }

  return result;
};

const koboFormat = (val: number | string) => {
  let formattedValue = Number(val) / 100;
  return Number(formattedValue)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,");
};

export const handleTypeNumberControl = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  const regex = new RegExp(
    /(^\d*$)|(Backspace|Tab|Delete|ArrowLeft|ArrowRight)/
  );
  let isValidCombo = false;
  const ctrl = e.ctrlKey ? e.ctrlKey : e.which === 17 ? true : false;
  const cmd = e.metaKey ? true : false;

  if (
    (e.which == 86 && ctrl) ||
    (e.which == 67 && ctrl) ||
    (e.which == 88 && ctrl)
  )
    isValidCombo = true;
  if (
    (e.which == 86 && cmd) ||
    (e.which == 67 && cmd) ||
    (e.which == 88 && cmd)
  )
    isValidCombo = true;

  if (
    e.which === 38 ||
    e.which === 40 ||
    (!e.key.match(regex) && !isValidCombo)
  ) {
    e.preventDefault();
  }
};

const extractMfb = async () => {
  let hostName = window.location.hostname;
  let mfb;

  if (
    !hostName.includes("qoreonline") &&
    !hostName.includes("qore.build") &&
    hostName != "localhost"
  ) {
    mfb = hostName;
  } else {
    if (process.env.REACT_APP_ENVIRONMENT === "production") {
      hostName = hostName.replace("www.", "");

      if (!hostName.includes("ibank.")) {
        mfb = hostName.split(".")[0];
      } else {
        mfb = await localStorage.getItem("alias");
      }
    } else {
      mfb =
        (await localStorage.getItem("alias")) || "core-ibanking.netlify.app";
    }
  }

  console.log({ mfb, hostName });

  return { hostName, mfb };
};

const setAppMetaData = (institutionData: any) => {
  let link = document.querySelector("link[rel~=icon") as HTMLInputElement | any;
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.getElementsByTagName("head")[0].appendChild(link);
  }
  if (institutionData.favIcon) link.href = institutionData.favIcon; // 'https://www.google.com/s2/favicons?domain=dev.to&sz=512'
  document.title = institutionData.name;
};

const detectWrap = (className: string, initialHeight: number) => {
  let isWrapped = false;
  const item: any = document.querySelector(`.${className}`);
  const currentHeight = item?.clientHeight;
  //console.log({initialHeight, currentHeight})
  if (currentHeight > initialHeight) isWrapped = true;

  return isWrapped;
};

const wait = (time: number) => new Promise((res) => setTimeout(res, time));

const retrieveAPIValidationMsg = (errorObj: any) => {
  const errorProperties = Object.keys(errorObj);
  const message = errorObj[errorProperties[0]][0];

  return message;
};

const handleFeedClick = (link: string) => {
  if (link) {
    const feedLink = document.createElement("a");
    document.body.appendChild(feedLink);
    feedLink.href = link;
    feedLink.target = "_blank";
    feedLink.click();
    document.body.removeChild(feedLink);
  }
};

const getAccountDeletionFlowType = () => {
  let deletionFlowType = "domain";
  const hostName = window.location.hostname;

  if (hostName?.split(".")[0] === "ibank") {
    deletionFlowType = "alias";
  }

  return deletionFlowType;
};

export default {
  routeData,
  toTitleCase,
  getPageTitle,
  requestMessage,
  catchErrMsg,
  erroMessage,
  splitCamelCase,
  onboardingProgressBarLength,
  moneyFormat,
  loglite,
  getCharacterToDispay,
  koboFormat,
  extractMfb,
  setAppMetaData,
  detectWrap,
  wait,
  handleFeedClick,
  getAccountDeletionFlowType,
};
